// Tuning constants. Tweak here; never hardcode in subsystems.
// Tile colors are referenced by both the Three.js scene and the season manager,
// so this file is the single source of truth.

export const Seasons = Object.freeze({
  PREP: 'PREP',
  PLANT: 'PLANT',
  MAINTAIN: 'MAINTAIN',
  HARVEST: 'HARVEST',
})

export const SeasonOrder = [Seasons.PREP, Seasons.PLANT, Seasons.MAINTAIN, Seasons.HARVEST]

// Single knob that scales every season's click goal. Bump to make the
// PREP→PLANT→MAINTAIN→HARVEST loop feel slower / weightier; drop to make
// each cycle fly by. Base values below were PREP 30 / PLANT 30 / MAINTAIN 60
// / HARVEST 30 (1× pacing).
export const SeasonPaceMultiplier = 3

// Each season: clickGoal, perClickYield (Mao Coin), tileColor (hex int for Three.js).
// clickGoal = base × SeasonPaceMultiplier — calculated below so a single knob
// rebalances all four seasons together.
// `emoji` drives the always-visible HUD season glyph (Layer 1); `plantScale`
// scales the rice-paddy diorama's height so the field visibly grows through the
// cycle (Layer 2) — short nubs in PREP → full + golden by HARVEST.
export const SeasonConfig = Object.freeze({
  [Seasons.PREP]: {
    name: 'PREP',
    label: 'PREPARATION',
    clickGoal: 30 * SeasonPaceMultiplier,
    perClickYield: 0,
    tileColor: 0x8b6f3d, // brown — untilled
    paddyColor: 0x7d6038, // ploughed, puddled mud — not flooded yet
    emoji: '🌱',
    plantScale: 0.35,    // short nubs — fields just turned
    tip: 'Prep the rice fields — use the clicker to ready the soil. More clicks now mean more rice later.',
  },
  [Seasons.PLANT]: {
    name: 'PLANT',
    label: 'PLANTING',
    clickGoal: 30 * SeasonPaceMultiplier,
    perClickYield: 0,
    tileColor: 0xc9a876, // tan — tilled
    paddyColor: 0x6b95a8, // newly flooded: shallow blue-grey water between the rows
    emoji: '💧',
    plantScale: 0.62,    // young shoots in flooded paddies
    tip: 'Rice planting time! Click as fast as you can to plant more rice.',
  },
  [Seasons.MAINTAIN]: {
    name: 'MAINTAIN',
    label: 'MAINTENANCE',
    clickGoal: 60 * SeasonPaceMultiplier,
    perClickYield: 5,
    tileColor: 0x4f7942, // green — sprouting
    paddyColor: 0x4f8a8c, // flooded all season: deeper blue-green water
    emoji: '✨',
    plantScale: 1.0,     // full, lush green
    tip: 'Maintain your rice fields — rapid clicks set up a bigger harvest bounty.',
  },
  [Seasons.HARVEST]: {
    name: 'HARVEST',
    label: 'HARVEST',
    clickGoal: 30 * SeasonPaceMultiplier,
    perClickYield: 50,
    tileColor: 0xd4a548, // gold — mature
    paddyColor: 0xb89a62, // drained before cutting: dry tan-brown ground
    emoji: '🌾',
    plantScale: 1.0,     // mature, heavy heads
    tip: 'Harvest your rice! Click as fast as possible for maximum Mao Coin!',
  },
})

// Goons — auto-clicker citizens. Phase 2 starts with one type; more land later.
// cost(n) = baseCost * costGrowth^n, where n = number already owned.
// clicksPerSec is the rate at which each goon fires registerClick().
export const GoonTypes = Object.freeze({
  PEASANT_CAT: 'PEASANT_CAT',
  SCRIBE_CAT: 'SCRIBE_CAT',
  NOBLE_CAT: 'NOBLE_CAT',
})

export const GoonConfig = Object.freeze({
  [GoonTypes.PEASANT_CAT]: {
    id: GoonTypes.PEASANT_CAT,
    name: 'Peasant Cat',
    description: 'Tends the field at a steady pace.',
    baseCost: 50,
    costGrowth: 1.15,
    clicksPerSec: 0.5,
    buyable: true,
    maxOwned: 100,
  },
  [GoonTypes.SCRIBE_CAT]: {
    id: GoonTypes.SCRIBE_CAT,
    name: 'Scribe Cat',
    description: 'A learned cat. Works thrice as fast.',
    baseCost: 500,
    costGrowth: 1.18,
    clicksPerSec: 1.5,
    buyable: true,
    maxOwned: 100,
  },
  [GoonTypes.NOBLE_CAT]: {
    id: GoonTypes.NOBLE_CAT,
    name: 'Noble Cat',
    description: 'Bred from a Peasant union. Works royally.',
    baseCost: 0,
    costGrowth: 1,
    clicksPerSec: 4,
    buyable: false,
  },
})

// Beyond this owned count we stop spawning new on-field meshes per goon type
// — the HUD owned-counter keeps incrementing, but the paddy doesn't get noisier.
export const VisualGoonCap = 12

// FEED — temporary goon speed buff. Re-buying while active extends the timer.
export const FeedConfig = Object.freeze({
  cost: 60,              // minimum cost (floor for few/no citizens)
  costPerCitizen: 50,    // you feed each cat → cost scales: max(cost, perCitizen × citizens)
  durationMs: 60_000,
  goonRateBonus: 0.5,    // +50% to (1 + total goon rate mult)
})

// HUNGER — if the player doesn't FEED their cats periodically, peasants
// (and the hero) get visually skinny (body shrinks, head stays the same
// — the malnourished look) and eventually start dying off.
//   skinnyAfterMs   first state transition: well-fed → skinny
//   dyingAfterMs    second transition: skinny → dying (death rolls begin)
//   deathTickMs     while in "dying", how often we roll for a kill
//   deathChance     per-tick chance the roll picks a random visible peasant
//   skinnyBodyXZ    body's X/Z scale when starving (Y stays so the silhouette
//                   keeps its height — gives the "tiny waist, normal head" look)
// The stages, timed from the last FEED (the clock starts fresh every session — see feedManager.js): citizens go THIN, then wear a
// yellow CAUTION triangle, then a red CROSS, and from hospitalAfterMs on they are ADMITTED to the Hospital one at a time (a roll every
// deathTickMs with admitChance) instead of dying in the field. See src/game/hungerRules.js and HospitalConfig.
export const HungerConfig = Object.freeze({
  skinnyAfterMs: 5 * 60 * 1000,    // thin
  cautionAfterMs: 10 * 60 * 1000,  // + yellow ⚠ over the head
  dyingAfterMs: 15 * 60 * 1000,    // + red cross over the head ("critical")
  hospitalAfterMs: 20 * 60 * 1000, // from here on, admissions to the Hospital
  deathTickMs: 20 * 1000,          // how often the admission roll happens
  admitChance: 0.25,               // chance per roll that one citizen is admitted
  deathChance: 0.25,               // (old name of admitChance — kept for anything that still reads it)
  // Peasants / scribes / nobles shrink when their caretaker neglects them.
  skinnyBodyXZ: 0.45,
  // The HERO (the player) doesn't suffer the same way — they get FATTER from
  // hoarding wealth and refusing to feed the tribe. Body X/Z scales up so
  // the silhouette reads as a greedy, well-fed neglector while the head
  // stays normal (same "body morph, head intact" trick as the skinny look).
  heroFatBodyXZ: 1.55,
})

// TAX — passive tribute paid by citizens (goons). Independent of seasons;
// flows into wealth continuously. Offline catch-up at reload, capped.
export const TributeConfig = Object.freeze({
  PEASANT_CAT: 1, // m/sec per Peasant
  SCRIBE_CAT: 3, // m/sec per Scribe
  NOBLE_CAT: 15, // m/sec per Noble — high reward for marriage investment
})

// LINEAGE — consume 2 of `from` + cost → +1 of `produces`. This is the
// dynastic-breeding mechanic ("Royal Pairing" / "Establish Lineage"); the
// word "marriage" is now reserved for player-to-player relationships
// (see MarriageRules below + marriageManager.js).
export const LineageConfig = Object.freeze([
  {
    from: 'PEASANT_CAT',
    requires: 2,
    cost: 1000,
    produces: 'NOBLE_CAT',
    label: 'Establish a Noble Lineage',
  },
])

// MARRIAGE — player-to-player relationships. The requester can take up to
// `maxAsRequester` partners; an acceptor who is already married cannot
// request or accept new marriages (locks them as a 1-1 partner).
export const MarriageRules = Object.freeze({
  maxAsRequester: 5,     // (the older NPC-marriage code reads this; real marriages use maxWives / maxHusbands below)
  // Combined wealth is visible separately AND as a combined stat — neither
  // partner can spend the other's cash directly (it just stacks for show
  // and election eligibility).
  combinedWealthVisible: true,
  // REAL marriages between two players (docs/MARRIAGE-PLAN.md) — traditional rules: a male cat may have up to 5 wives, a female cat only one husband.
  genders: ['MALE', 'FEMALE'],
  maxWives: 5,
  maxHusbands: 1,
  proposalExpiresDays: [1, 3, 7],   // how long a proposal can be accepted
  defaultExpiresDays: 3,
  maxPendingSent: 5,                // proposals a player can have waiting
  retryAfterDeclineDays: 7,         // after a decline the same proposer must wait this long
  adoptMinHours: 24,                // a Mao must have been adopted this long before it can propose or accept
})

// CHILDREN — kittens born to player-to-player marriages. Requires the BIRTH
// license + active marriage + per-marriage cooldown. Each birth costs coin
// and appends a child record to the parent profile's `children` array,
// which the Registry's CHILDREN section displays. Future phases may grow
// children into adoptable goons / eligible heirs for inheritance.
export const BirthConfig = Object.freeze({
  cost: 2_500,
  cooldownMs: 30 * 1000, // 30s between births per marriage (testing-friendly)
  // Random name pool — picked uniformly when a child is born. Easy to extend.
  namePool: [
    'Mei', 'Jin', 'Lian', 'Bao', 'Hu', 'Yan', 'Ling', 'Wei', 'Shu',
    'Tao', 'Xiao', 'Yue', 'Feng', 'An', 'Hong', 'Lin', 'Ping', 'Qi',
  ],
})

// DIVORCE — the Emperor decrees a random split per case. Three flavors,
// each with the active emperor's signature insult. The "male" (the original
// requester) is decreed the larger share by tradition.
export const DivorceConfig = Object.freeze({
  splits: [
    { requesterPct: 0.8, label: 'Eighty-Twenty' },
    { requesterPct: 0.7, label: 'Seventy-Thirty' },
    { requesterPct: 0.5, label: 'Even Split' },
  ],
})

// MEMBERSHIP — free tier vs paid Kingdom Club. Real-money subscription
// ($9.99/mo via Stripe). The Stripe URL is wired in by the operator; for
// dev the redeem-code path flips membership locally without billing.
export const MembershipConfig = Object.freeze({
  tiers: {
    FREE: {
      id: 'FREE',
      name: 'Free',
      clickBonusMult: 1,           // no bonus
      registryAccess: 'public',    // can see stripped public profiles only
    },
    KINGDOM_CLUB: {
      id: 'KINGDOM_CLUB',
      name: 'Kingdom Club',
      clickBonusMult: 1.1,         // +10% on every paw click yield
      registryAccess: 'full',      // can see other players' full Registry sheets
    },
  },
  // Replace with the live Stripe Checkout link (recurring $9.99/mo).
  stripeCheckoutUrl: 'https://buy.stripe.com/test_PLACEHOLDER_kingdom_club_999_monthly',
  monthlyPriceUsd: 9.99,
  // Local dev path — typing this exact code in the membership panel toggles
  // KINGDOM_CLUB on without billing. Strip before production deployment.
  devRedeemCode: 'KINGDOM_CLUB_DEV',
})

// GOON TRAINING — per goon type, kingdom-wide level. Each level multiplies
// that goon type's click rate by (1 + levelBonus × level). Distinct from the
// PLAYER's education (degrees + majors), which is profile-scoped, not goon-scoped.
export const GoonTrainingConfig = Object.freeze({
  baseCost: 500,
  costGrowth: 2,
  levelBonus: 0.2,
  maxLevel: 10,
})

export const TaxOfflineCapMs = 8 * 60 * 60 * 1000 // 8 hours

// PLAYER PROFILE — TITLES, CAT COLORS, EDUCATION, CERTIFICATES, DEFECTION.
//
// At onboarding the player adopts a cat, picks a kingdom, picks a working-class
// title. Title gives a small mechanical lean. They then climb the education
// ladder (Certificates → AA → BA → MA → PHD, with majors) to specialize and
// (in a later phase) qualify for elite ranks.

// CLASSES — the Mao Kingdom Flow doc groups titles into four classes. IDs
// follow the diagram's two-digit scheme: 1=Royal, 1X=High, 2X=Middle, 3X=Common.
// Phase 5.1 only the Common class is selectable at onboarding; the rest are
// scaffolded for future Election-gated promotion.
export const ClassIds = Object.freeze({
  ROYAL: 'ROYAL',
  HIGH: 'HIGH',
  MIDDLE: 'MIDDLE',
  COMMON: 'COMMON',
})

export const ClassConfig = Object.freeze({
  [ClassIds.ROYAL]: { id: 'ROYAL', code: '1', name: 'Royal', startable: false },
  [ClassIds.HIGH]: { id: 'HIGH', code: '1X', name: 'High', startable: false },
  [ClassIds.MIDDLE]: { id: 'MIDDLE', code: '2X', name: 'Middle', startable: false },
  [ClassIds.COMMON]: { id: 'COMMON', code: '3X', name: 'Common', startable: true },
})

// TITLES — the working-class trio is selectable at onboarding. Higher-class
// titles are stubbed here so future phases can light them up without rework.
export const TitleTypes = Object.freeze({
  FARMER: 'FARMER',
  ARTISAN: 'ARTISAN',
  MERCHANT: 'MERCHANT',
  // High / Middle / Royal — defined for the data model; Phase 5.2+ surfaces them.
  IMPERIAL: 'IMPERIAL',
  NOBLE: 'NOBLE',
  GOVERNMENT: 'GOVERNMENT',
  MILITARY: 'MILITARY',
  PALACE: 'PALACE',
  CLERGY: 'CLERGY',
  EMPEROR: 'EMPEROR',
  EMPRESS: 'EMPRESS',
  PRINCE: 'PRINCE',
  PRINCESS: 'PRINCESS',
})

// Each title carries its class membership + a code matching the diagram.
// Only titles with full `pawYieldMult / buildingYieldMult / goonRateMult` are
// playable now; the higher-class entries are scaffolds (no bonus yet).
export const TitleConfig = Object.freeze({
  [TitleTypes.FARMER]: {
    id: 'FARMER', name: 'Farmer', subtitle: '农', code: '31', classId: ClassIds.COMMON,
    description: '+20% paw click yield. The hands that feed the kingdom.',
    pawYieldMult: 1.2, buildingYieldMult: 1.0, goonRateMult: 1.0, taxMult: 1.0,
  },
  [TitleTypes.ARTISAN]: {
    id: 'ARTISAN', name: 'Artisan', subtitle: '工', code: '32', classId: ClassIds.COMMON,
    description: '+25% building yield bonuses. The hands that shape the kingdom.',
    pawYieldMult: 1.0, buildingYieldMult: 1.25, goonRateMult: 1.0, taxMult: 1.0,
  },
  [TitleTypes.MERCHANT]: {
    id: 'MERCHANT', name: 'Merchant', subtitle: '商', code: '33', classId: ClassIds.COMMON,
    description: '+15% tax tribute. The hands that move coin through the kingdom.',
    pawYieldMult: 1.0, buildingYieldMult: 1.0, goonRateMult: 1.0, taxMult: 1.15,
  },
  // ── Elected titles (5.2.1) — bonuses live; eligibility gated by ElectionConfig
  [TitleTypes.NOBLE]: {
    id: 'NOBLE', name: 'Noble', subtitle: '贵', code: '12', classId: ClassIds.HIGH,
    description: '+25% to all yields. Member of the kingdom council.',
    pawYieldMult: 1.25, buildingYieldMult: 1.25, goonRateMult: 1.25, taxMult: 1.25,
  },
  [TitleTypes.MILITARY]: {
    id: 'MILITARY', name: 'Military', subtitle: '兵', code: '21', classId: ClassIds.MIDDLE,
    description: '+50% goon click rate; +10% paw yield. Commander of the rice troops.',
    pawYieldMult: 1.1, buildingYieldMult: 1.0, goonRateMult: 1.5, taxMult: 1.0,
  },
  [TitleTypes.CLERGY]: {
    id: 'CLERGY', name: 'Clergy', subtitle: '僧', code: '23', classId: ClassIds.MIDDLE,
    description: '+10% across the board. The kingdom\'s spiritual hand.',
    pawYieldMult: 1.1, buildingYieldMult: 1.1, goonRateMult: 1.1, taxMult: 1.1,
  },
  // ── Higher-class scaffolds (no bonuses yet — flesh out post-Election when
  //    the diagram's Royal / Imperial / Government / Palace paths land).
  [TitleTypes.IMPERIAL]:   { id: 'IMPERIAL',   name: 'Imperial',   subtitle: '帝', code: '11', classId: ClassIds.HIGH },
  [TitleTypes.GOVERNMENT]: { id: 'GOVERNMENT', name: 'Government', subtitle: '官', code: '13', classId: ClassIds.HIGH },
  [TitleTypes.PALACE]:     { id: 'PALACE',     name: 'Palace',     subtitle: '宫', code: '22', classId: ClassIds.MIDDLE },
  [TitleTypes.EMPEROR]:    { id: 'EMPEROR',    name: 'Emperor',    subtitle: '皇', code: '1',  classId: ClassIds.ROYAL },
  [TitleTypes.EMPRESS]:    { id: 'EMPRESS',    name: 'Empress',    subtitle: '后', code: '2',  classId: ClassIds.ROYAL },
  [TitleTypes.PRINCE]:     { id: 'PRINCE',     name: 'Prince',     subtitle: '王', code: '3',  classId: ClassIds.ROYAL },
  [TitleTypes.PRINCESS]:   { id: 'PRINCESS',   name: 'Princess',   subtitle: '主', code: '4',  classId: ClassIds.ROYAL },
})

// ELECTION — caps, prerequisites, NPC name pools per kingdom. Phase 5.2.1
// fills rosters with NPCs; Phase 5.2.2 will swap NPC entries for real players.
export const ElectedTitleIds = [TitleTypes.NOBLE, TitleTypes.MILITARY, TitleTypes.CLERGY]

// Prereq vocabulary (CR-6 degree model — subjects × tiers):
//   `minTier`              — hold ANY subject at this tier or higher
//   `requiredSubject`      — hold this specific subject at `minTier`+
//   `requiredSubjectOneOf` — hold at least one of these subjects at `minTier`+
// Tiers: CERT < MASTERS < PHD (see DegreeTierOrder).
export const ElectionConfig = Object.freeze({
  [TitleTypes.NOBLE]:    { cap: 4, minTier: 'MASTERS' },
  [TitleTypes.MILITARY]: { cap: 2, minTier: 'CERT', requiredSubject: 'MILITARY' },
  [TitleTypes.CLERGY]:   { cap: 2, minTier: 'CERT', requiredSubjectOneOf: ['DIVINITY', 'EDUCATION'] },
})

// Placeholder NPC names so the rosters look populated before real multiplayer
// lands. Three per kingdom matches the visual identity of the historical court.
export const NpcNamesByKingdom = Object.freeze({
  WEI: ['Cao Hua', 'Xun Yu', 'Sima Yi', 'Zhang He', 'Xiahou Dun', 'Cao Ren'],
  HAN: ['Liu Shan', 'Zhang Fei', 'Guan Yu', 'Zhao Yun', 'Ma Chao', 'Huang Zhong'],
  WU: ['Sun Ce', 'Lu Meng', 'Lu Xun', 'Zhou Tai', 'Gan Ning', 'Taishi Ci'],
})

// Helper for onboarding + future class-pickers: titles a player can START as.
export const StartableTitleIds = [TitleTypes.FARMER, TitleTypes.ARTISAN, TitleTypes.MERCHANT]

// ADOPTION — FREE branch enrolls a Common-class title at no cost. PAID branch
// fast-tracks to higher classes, skipping the election grind. Prices keyed by
// class follow the peg table at: $5 / $25 / $100. Royal sits at $100 (1Bm).
export const AdoptionPricing = Object.freeze({
  [ClassIds.COMMON]: 0,
  [ClassIds.MIDDLE]: 5_000_000,
  [ClassIds.HIGH]: 25_000_000,
  [ClassIds.ROYAL]: 100_000_000,
})

// ART SERIES — the three NFT image styles from the Mao Kingdom Flow doc.
// `dir` is the subfolder under /public/models/ where .glb assets for that
// series live. The asset loader looks at /models/<dir>/<assetKey>.glb at
// boot; missing files fall back silently to the voxel placeholder geometry.
export const SeriesIds = Object.freeze({
  SERIES_1: 'SERIES_1',
  SERIES_2: 'SERIES_2',
  SERIES_3: 'SERIES_3',
  SERIES_4: 'SERIES_4',
})

export const SeriesConfig = Object.freeze({
  [SeriesIds.SERIES_1]: { id: 'SERIES_1', name: 'Series 1 — Line Art', dir: 'series-1' },
  [SeriesIds.SERIES_2]: { id: 'SERIES_2', name: 'Series 2 — AI Art (Midjourney)', dir: 'series-2' },
  [SeriesIds.SERIES_3]: { id: 'SERIES_3', name: 'Series 3 — 3D Voxel', dir: 'series-3' },
  [SeriesIds.SERIES_4]: { id: 'SERIES_4', name: 'Series 4 — Low-Poly Asian', dir: 'series-4' },
})

export const DefaultSeriesId = SeriesIds.SERIES_4

// FIELD WORK — rapid clicking sends a crew of citizens to run the rows of the nearest rice paddy (src/game/fieldWork.js, scene/peasants.js).
export const FieldWorkConfig = Object.freeze({
  startClicks: 4,       // this many clicker presses inside startWindowMs count as "rapid clicking"
  startWindowMs: 3000,
  stopIdleMs: 2500,     // no press for this long = the clicking has stopped: the crew goes back to roaming
  crew: 4,              // how many citizens work the rows at once (peasants first; scribes only if there are not enough peasants)
  passes: 3,            // sweeps over each worker's rows of a paddy before it walks on to the next-nearest one
  recruitEveryMs: 500,  // how often the crew is topped up while the clicking goes on
  rowEndMarginM: 0.5,   // stay this far (source metres) inside each end of a row
})

// WILD GOONS — red-tinted antagonist cats that periodically appear on the
// field, wander aimlessly, and either:
//   • walk into a Temple → get converted to a loyal Peasant Cat (free recruit)
//   • run out of lifespan → leave silently
// A Temple's purpose now extends beyond yield bonuses: it's a conversion
// engine for whatever wild goons happen to wander past.
export const WildGoonConfig = Object.freeze({
  spawnIntervalMs: 30_000, // every 30s, try to spawn one
  lifespanMs: 45_000, // wild goon despawns after 45s if uncaught
  maxAlive: 3, // never more than this on the field at once
  raidSpeedMult: 2, // while a raid is on, every goon on the field moves this many times faster than its everyday wander
  // Collision radii. Originally tuned for `Field.catScale = 1.0`; after cats
  // shrank to 0.1625 these were retuned to match the new visual footprints —
  // a cat body is now ~0.11 world units wide, so anything north of ~0.2 felt
  // like ghosts touching across half a tile. Temple is still full-size so its
  // conversion sphere stays a bit larger than the kill radii.
  conversionRadius: 0.35, // wild goon center → Temple center distance for a hit
  killRadius: 0.12,       // wild goon center → peasant center for a kill
  heroKillRadius: 0.15,   // hero center → wild goon center for a slay
  // Mao Coin paid for slaying a wild goon (hero touch OR gun-mode shot).
  // Temple-conversion is NOT bountied — you already get a free peasant out
  // of it, so a coin bounty on top would over-reward the safe play.
  bounty: 100,
  // When a raid ends (the player is back) the goons retreat — but not all at once. Each picks a pace at random
  // (world units per second), so the slow ones give the player a chance to shoot them on the way out.
  fleeTiers: [
    { name: 'fast',   share: 0.33, speed: 1.0 },
    { name: 'medium', share: 0.33, speed: 0.5 },
    { name: 'slow',   share: 0.34, speed: 0.25 },
  ],
  bodyColor: 0x5a2020,
  headColor: 0x3a1010,
  earColor: 0x1a0505,
  eyeColor: 0xff4444,
})

// IDLE / ANTI-AFK — instead of pausing, the kingdom's Emperor fines the player
// for negligence. Two stages:
//   • warnAfterMs idle → soft banner ("citizens grow restless")
//   • fineAfterMs idle → Emperor confiscates a chunk of treasury (initial fine)
//                       + recurring fines every recurringIntervalMs
//                       + goon rate drops, tax tribute is intercepted
// Activity (mousemove / click / keydown / touch) clears the state and restores
// normal multipliers. The accumulated grievance fuels the long-game motivation
// to topple the Emperor in a future phase.
// PLACEMENT — buying a building is a short flow: pick it, drop it on a plot, rotate it, then Confirm / Reposition / Cancel.
// Cancelling gives the coin back minus a penalty (a sink: nothing is free); Reposition is a free move.
export const PlacementConfig = Object.freeze({
  cancelRefundFrac: 0.8,   // cancel → you get 80% of the price back (20% penalty fee)
  rotateSteps: 6,          // a hex tile has 6 facings, 60° each
})

// RAID DIRECTOR — raids as a regular part of play (src/game/raidDirector.js). Three real sources, all announced first:
//   • a scheduled raid every few minutes while you are actively playing,
//   • raids that follow real game events (a Drought or Bandits event emboldens raiders), and
//   • a danger level that rises with the wealth you hold, so a bigger kingdom attracts bigger raids.
// Nothing is invented: a warning always names the real number of goons and the real edge they arrive from.
export const RaidDirectorConfig = Object.freeze({
  enabled: true,
  firstRaidAfterMs: [180_000, 270_000],   // earliest first scheduled raid of a session (3–4.5 min of play)
  intervalMs: [180_000, 330_000],         // between scheduled raids (3–5.5 min)
  minGapMs: 120_000,                      // never closer together than this, whatever the source
  warnMs: 20_000,                         // how long before arrival the smoke signal goes up
  maxDurationMs: 75_000,                  // a raid ends when its goons are gone, or after this (survivors then retreat)
  // Danger level from the wealth you hold. `size` = goons per raid (inclusive range).
  tiers: [
    { name: 'Quiet',     minWealth: 0,           size: [3, 6] },
    { name: 'Watched',   minWealth: 50_000,      size: [5, 9] },
    { name: 'Notorious', minWealth: 500_000,     size: [8, 15] },
    { name: 'Feared',    minWealth: 5_000_000,   size: [12, 22] },
    { name: 'Legendary', minWealth: 50_000_000,  size: [18, 32] },
  ],
  // Rapid clicking on the clicker for rapidMs straight (same "rapid" as the field-working cat, FieldWorkConfig), with at least minPresses
  // presses in that time (2 a second — a slow tap every couple of seconds doesn't count), brings a MASSIVE clan:
  // announced like any raid, from one random edge, size = the danger tier's size × sizeMult, at least minSize and at most maxSize.
  fromClicking: { rapidMs: 30_000, minPresses: 60, sizeMult: 2.5, minSize: 15, maxSize: 45, why: 'All that frantic clicking has stirred up a whole goon clan.' },
  // Real events that can bring a raid: chance, delay before the warning, and a size multiplier.
  fromEvents: {
    DROUGHT:  { chance: 0.6,  delayMs: 12_000, sizeMult: 1.0, why: 'Bandits are taking advantage of the drought.' },
    BANDITS:  { chance: 1.0,  delayMs: 15_000, sizeMult: 1.0, why: 'The bandits who just robbed you are coming back for more.' },
    FESTIVAL: { chance: 0.25, delayMs: 12_000, sizeMult: 0.7, why: 'The festival crowds and coin have drawn thieves.' },
  },
})

export const IdleConfig = Object.freeze({
  warnAfterMs: 15_000,
  fineAfterMs: 60_000,
  recurringIntervalMs: 60_000,
  initialFinePct: 0.05,
  recurringFinePct: 0.01,
  // Goon click-rate multipliers per idle state. Models real-world worker
  // behavior: when the boss is actively present (ACTIVE) everyone works at
  // full pace; the moment attention lapses (WARNING) productivity drops
  // because the workers know no one is watching; an active fine (FINED)
  // means the kingdom is in revolt — almost nothing gets done.
  activeGoonRateMult: 0.65,   // engaged play — slowed from 1.0 so tally doesn't race
  warningGoonRateMult: 0.4,   // boss looked away ~60s → workers slack off
  finedGoonRateMult: 0.15,    // 90s+ idle → kingdom in open rebellion (was 0.25)
  finedTaxMult: 0,
  // RAID — if the player is FINED and STILL doesn't engage, the bandit
  // tribes smell weakness and SWARM in to slaughter citizens and trample
  // fields. This must FEEL like an invasion — goons pour in by the wave,
  // not a polite one-at-a-time trickle. An opening wave hits the instant the
  // raid begins, then reinforcements arrive in batches until the cap is full
  // or the player returns to active (which scatters the whole horde).
  raidAfterFineMs: 30_000,      // 30s into FINED state → raid begins (60s fine + 30s = 90s)
  raidMaxAlive: 100,            // concurrent wild goons cap during a raid (a full horde)
  raidSpawnIntervalMs: 1_800,   // reinforcement wave cadence (down from 30s normal)
  raidSpawnBatch: 25,           // goons dropped per wave (incl. the opening wave)
})

// RAID DAMAGE — during an active raid, wild goons don't just kill citizens.
// They also set fire to buildings and trample/burn farm plots. After the
// raid ends, the player chooses between paying coin for instant repair or
// dedicating their citizens (halting all goon income) for a labor repair.
export const RaidDamageConfig = Object.freeze({
  // Per-frame chance any wild goon damages a building / plot it's standing
  // on while a raid is active. Tuned low so a goon usually only torches
  // one or two things during its lifespan.
  damageChancePerSec: 0.08,
  // Color a damaged FARM plot reverts to — scorched-earth black/brown.
  scorchedTileColor: 0x2a1a0e,
  // Repair options offered when damages exist.
  paidRepairCostPerDamage: 1_500,        // coin per damaged thing
  laborRepairMsPerDamage: 12_000,        // 12s per damaged thing — adds up fast
  laborGoonRateMult: 0,                  // citizens busy = zero income during labor
})

// EMPERORS — the antagonist of each kingdom: who fines you when you're AFK
// and what they say when they do. Future phase: a "topple the Emperor" arc
// unlocks at a wealth threshold so the player can fight back.
// An armed reticle (key 1 = red crosshair, key 2 = friendly cursor) that goes unused this long fades out and must be re-armed.
export const ToolAutoOffMs = Object.freeze({ DEFENSE: 10_000, POSITIVE: 30_000 })

// If the player hasn't used the clicker for this long, its outer ring starts a soft green↔white glow (a nudge, not an alert).
export const ClickerNudgeMs = 90_000

export const KingdomEmperors = Object.freeze({
  WEI: {
    name: 'Cao Cao',
    cn: '曹操',
    warn: '"Asleep at the throne already? A peasant tends his field better than you tend my kingdom. Return — or feed my coffers."',
    scold: '"Pathetic. A neglected field is a wasted kingdom. I shall keep what you forgot to count."',
    recurring: '"Still absent? My treasury thanks you for your stupidity."',
    zoning: (building, plot, fine) =>
      `"A ${building} on ${plot} land? You presume to ZONE my kingdom with the wit of a turnip. ${fine} forfeit. Tear it down and try again — on the right soil, simpleton."`,
    friendlyFireCat: (catName) =>
      `"You shot your own ${catName}? A shepherd who slaughters his own flock. The cats of Wei deserve a less pitiable caretaker. Pull yourself together — or step aside for someone competent."`,
    friendlyFireHero: () =>
      `"You aimed at your own face, Mao? I would laugh, but the spectacle is too sad. Even your own reflection is unsafe under your custody. Try not to kill yourself before tribute day."`,
    divorce: (split, requesterPct) =>
      split === 0.5
        ? `"An even split? How modern of you both. I, Cao Cao, oversee fair partitions only when neither side deserves the trouble of insult. ${Math.round(requesterPct * 100)}% to each — and may you both regret it equally."`
        : `"By imperial decree, ${Math.round(requesterPct * 100)}% goes to the requester. Tradition is a comfort to fools who cannot negotiate. Off with you both — and pray for better matches next time."`,
    raid: `"The bandit tribes have heard you abandoned your kingdom. I will not stop them. Watch your peasants die — or come back and fight."`,
  },
  HAN: {
    name: 'Liu Shan',
    cn: '劉禪',
    warn: '"Even I — who lost a kingdom for napping — am more present than you. Embarrassing. Return before I forget you exist."',
    scold: '"Father said to tend the kingdom. I did not listen. Neither did you. I take my share, fool."',
    recurring: '"Another lapse? You are duller than I am. More tribute for me."',
    zoning: (building, plot, fine) =>
      `"A ${building} on ${plot}?? Even I know better, and I am Liu Shan. ${fine} into my coffers. Move that monstrosity to a plot it actually fits — the courtiers are laughing at you."`,
    friendlyFireCat: (catName) =>
      `"You shot your own ${catName}? Even I, who lost a kingdom, never shot a citizen of Han. You're worse than I am — and that takes some doing. Be a better caretaker, or there will be no tribe left to neglect."`,
    friendlyFireHero: () =>
      `"You shot yourself? My ancestors are speechless. I am speechless. Try aiming at the enemy next time, you embarrassing creature."`,
    divorce: (split, requesterPct) =>
      `"Divorce already? My own household never lasted long either. The decree: ${Math.round(requesterPct * 100)}% to the requester. Live with it — or marry less foolishly next time. Probably both."`,
    raid: `"The bandits are at your gates because you stopped tending the kingdom. I lost mine the same way. The peasants who die today die because of you."`,
  },
  WU: {
    name: 'Sun Hao',
    cn: '孫皓',
    warn: '"Negligent worm. The block is sharp and your treasury is plump. Return or I help myself."',
    scold: '"Inattention is treason. Your treasure pays the price, coward."',
    recurring: '"Still wandering, you spineless thing? The block is always sharp. Pay."',
    zoning: (building, plot, fine) =>
      `"A ${building} on a ${plot} plot? Treason against zoning, vermin. ${fine} stripped from your treasury. Relocate it to a proper plot — or the next levy comes off your skin."`,
    friendlyFireCat: (catName) =>
      `"You butchered your own ${catName}, you incompetent worm? Even I do not waste my own subjects. A caretaker of Wu does not slaughter his tribe — only mine does that. Try harder, or join your dead."`,
    friendlyFireHero: () =>
      `"You shot yourself? Wu has executed many fools, but never one this thorough. Save the block the trouble — or aim at an enemy, just this once."`,
    divorce: (split, requesterPct) =>
      split === 0.8
        ? `"Eighty to the requester. The natural order. Your spouse will weep — let them. Their tears irrigate no farm in Wu."`
        : `"${Math.round(requesterPct * 100)}% to the requester. Generous, by Wu standards. Now leave my court before I tax the divorce itself."`,
    raid: `"You are too lazy even to defend your own peasants. The bandits descend. I shall not lift a sword for a kingdom whose lord has already abandoned it. Die or wake up."`,
  },
})

// AssetKeys — every model the scene asks for. Adding a new key requires (a)
// listing it here and (b) the scene module that consumes it. The loader will
// look for /public/models/<series-dir>/<key>.glb at boot.
export const AssetKeys = Object.freeze({
  CAT_HERO: 'cat_hero',
  PEASANT_CAT: 'peasant_cat',
  SCRIBE_CAT: 'scribe_cat',
  NOBLE_CAT: 'noble_cat',
  BUILDING_GRANARY: 'building_granary',
  BUILDING_TEMPLE: 'building_temple',
  BUILDING_WATCHTOWER: 'building_watchtower',
  BUILDING_HOSPITAL: 'building_hospital',
  BUILDING_SCHOOL: 'building_school',
  BUILDING_SHOP: 'building_shop',
})

export const CatColors = Object.freeze([
  { id: 'CREAM', body: 0xf4ead5, head: 0xb89a6c, ear: 0x8e7448, label: 'Cream' },
  { id: 'GREY', body: 0xb5b5b5, head: 0x8a8a8a, ear: 0x666666, label: 'Grey' },
  { id: 'BLACK', body: 0x2c2c2c, head: 0x1a1a1a, ear: 0x0d0d0d, label: 'Black' },
  { id: 'GINGER', body: 0xe09b5b, head: 0xa86f3c, ear: 0x7d4f25, label: 'Ginger' },
  { id: 'SNOW', body: 0xf0eee5, head: 0xd6d2c4, ear: 0xa8a497, label: 'Snow' },
  { id: 'TABBY', body: 0x8b6f4e, head: 0x664f37, ear: 0x4a3a26, label: 'Tabby' },
])

// LICENSES — regulatory permits (one of each). Business carries a bonus;
// Marriage gates the marriage feature; Birth scaffolds for a future kitten
// mechanic. From the Mao Kingdom Flow doc: Business / Marriage / Birth / TBD.
export const LicenseTypes = Object.freeze({
  BUSINESS: 'BUSINESS',
  MARRIAGE: 'MARRIAGE',
  BIRTH: 'BIRTH',
})

export const LicenseConfig = Object.freeze({
  [LicenseTypes.BUSINESS]: {
    id: 'BUSINESS', name: 'Business License', cost: 10_000,
    bonusKey: 'taxBonus', bonusValue: 0.1,
    desc: '+10% tax tribute. Permits trade.',
  },
  [LicenseTypes.MARRIAGE]: {
    id: 'MARRIAGE', name: 'Marriage License', cost: 10_000,
    bonusKey: null, bonusValue: 0,
    desc: 'Required to marry cats.',
  },
  [LicenseTypes.BIRTH]: {
    id: 'BIRTH', name: 'Birth License', cost: 5_000,
    bonusKey: null, bonusValue: 0,
    desc: 'Permits future kitten births.',
  },
})

// DEGREES — retired in CR-6. The old AA/BA/MA/PHD + diploma model is replaced
// by the 8-subject × 3-tier self-education system (DegreeSubjects / DegreeTiers,
// below) where each tier is earned by passing a Three-Kingdoms quiz. Degrees now
// feed Honor (honor.js) and PhDs raise the click base (seasonManager) — they no
// longer route into the pawYield/goonRate/tax/culture bonus buckets.

// DEFECTION — pay to change home kingdom. The player's wealth + buildings +
// goons all move with them. The old kingdom resets to empty.
export const DefectionConfig = Object.freeze({
  cost: 50_000,
})

// MAO COIN — the in-world currency. Pegged to USD at micro-cent levels per
// the Mao Kingdom Flow doc:
//   1m              = $0.0001
//   10,000m         = $0.01
//   1,000,000m      = $1
//   1,000,000,000m  = $1,000  (commonly displayed as "1b")
//
// Internal numbers stay in raw Mao-coin units (integers) for precision.
// The USD value is derived view-only.
export const MaoCoinConfig = Object.freeze({
  symbol: 'm',
  longName: 'Mao Coin',
  // Locked peg: $1 = 1,000,000m (matches pegTable + the coin packs below).
  usdPerCoin: 0.000001,
  // Pegging table (for the OVERVIEW page).
  pegTable: [
    { coins: 1, usd: 0.000001 },
    { coins: 10_000, usd: 0.01 },
    { coins: 100_000, usd: 0.10 },
    { coins: 1_000_000, usd: 1 },
    { coins: 10_000_000, usd: 10 },
    { coins: 50_000_000, usd: 50 },
    { coins: 100_000_000, usd: 100 },
    { coins: 1_000_000_000, usd: 1_000 },
  ],
})

// COIN PACKS — the ONLY real-money product (CR-7). USD buys Mao Coin at the
// $1 = 1,000,000m peg; bigger packs add a bonus to reward larger buy-ins
// (pay-to-progress). No cash-out, no subscription. Stripe sells these as
// one-time purchases; `bonus` coins are granted on top of `coins`.
export const CoinPacks = Object.freeze([
  { id: 'PACK_1M',  label: 'Pouch',  coins: 1_000_000,  bonus: 0,         priceUsd: 1 },
  { id: 'PACK_5M',  label: 'Chest',  coins: 5_000_000,  bonus: 500_000,   priceUsd: 5,  best: false },
  { id: 'PACK_10M', label: 'Vault',  coins: 10_000_000, bonus: 2_000_000, priceUsd: 10, best: true },
])
export const CoinPackById = Object.freeze(
  Object.fromEntries(CoinPacks.map((p) => [p.id, p])),
)

// Buildings — one per tile, occupies a tile slot, persists in save.
// yieldBonus is a flat Mao Coin amount added to every paw click.
// goonRateMult is added to (1 + sum) — i.e. 0.2 means +20% goon click rate.
export const BuildingTypes = Object.freeze({
  GRANARY: 'GRANARY',
  TEMPLE: 'TEMPLE',
  WATCHTOWER: 'WATCHTOWER',
  HOSPITAL: 'HOSPITAL',
  SCHOOL: 'SCHOOL',
  SHOP: 'SHOP',
})

export const BuildingConfig = Object.freeze({
  [BuildingTypes.GRANARY]: {
    id: BuildingTypes.GRANARY,
    name: 'Granary',
    description: '+5m per click',
    cost: 200,
    yieldBonus: 5,
    goonRateMult: 0,
    color: 0x8b5a2b,
    height: 0.6,
    // Granaries store the rice harvest — they only belong on a Farm plot.
    allowedPlotTypes: ['FARM'],
  },
  [BuildingTypes.TEMPLE]: {
    id: BuildingTypes.TEMPLE,
    name: 'Temple',
    description: '+10m per click',
    cost: 500,
    yieldBonus: 10,
    goonRateMult: 0,
    color: 0xa83c3c,
    height: 0.9,
    // Temples serve the household — Home or Residential plots only.
    allowedPlotTypes: ['HOME', 'RESIDENTIAL'],
  },
  [BuildingTypes.WATCHTOWER]: {
    id: BuildingTypes.WATCHTOWER,
    name: 'Watchtower',
    description: '+20% goon speed',
    cost: 1000,
    yieldBonus: 0,
    goonRateMult: 0.2,
    color: 0x707070,
    height: 1.2,
    // Watchtowers guard the merchant district — Commercial plots only.
    allowedPlotTypes: ['COMMERCIAL'],
  },
  // Three commercial-district structures. All three require a COMMERCIAL
  // plot to build on; the emperor's zoning decree fires if the player tries
  // to put them anywhere else.
  [BuildingTypes.HOSPITAL]: {
    id: BuildingTypes.HOSPITAL,
    name: 'Hospital',
    description: '+3m / click, +5% citizen speed (stacks, capped)',
    cost: 3_000,
    yieldBonus: 3,
    // Hospitals lift each citizen's per-tick output as they pass through.
    // Cumulative bonus is capped by BuildingBonusCap.goonRate so stacking
    // 20 hospitals doesn't break the economy.
    goonRateMult: 0.05,
    color: 0xe8d8d8, // off-white clinical
    height: 0.85,
    allowedPlotTypes: ['COMMERCIAL'],
  },
  [BuildingTypes.SCHOOL]: {
    id: BuildingTypes.SCHOOL,
    name: 'School',
    description: '+10% citizen speed (stacks, capped) — educated workers',
    cost: 2_500,
    yieldBonus: 0,
    goonRateMult: 0.10,
    color: 0x6c7da8, // institutional slate-blue
    height: 0.75,
    allowedPlotTypes: ['COMMERCIAL'],
  },
  [BuildingTypes.SHOP]: {
    id: BuildingTypes.SHOP,
    name: 'Shop',
    description: '+5m per click — coin from passing trade',
    cost: 1_500,
    yieldBonus: 5,
    goonRateMult: 0,
    color: 0xd49a3c, // warm commerce gold
    height: 0.55,
    allowedPlotTypes: ['COMMERCIAL'],
  },
})

// Diorama-tile identity for each building. The low-poly pack ships full hex
// dioramas (Tile_1..Tile_20) — entire mini-scenes, not standalone props. A
// placed building swaps its tile's surface for the matching diorama so each
// structure reads as a recognizable place. Numbers confirmed visually against
// the artist's reference renders (see the Tile Picker dev tool).
//   HOME=7 (cat manor), SCHOOL=20 (scholar courtyard), HOSPITAL=12,
//   TEMPLE=13 (shrine), SHOP=17 (market stall), GRANARY=10 (rice store),
//   WATCHTOWER=15 (terraced mound + switchback stair + moat).
export const BuildingDioramaTile = Object.freeze({
  HOME: 7, // retained for back-compat; the home now renders via HomeDioramaTile by level
  [BuildingTypes.SCHOOL]: 20,
  [BuildingTypes.HOSPITAL]: 12,
  [BuildingTypes.TEMPLE]: 13,
  [BuildingTypes.SHOP]: 17,
  [BuildingTypes.GRANARY]: 9, // STAND-IN [needs final granary model] — Tile_10 ("rice store"/shack) is now the starter home
  [BuildingTypes.WATCHTOWER]: 15,
})

// CR-5 home tiers — the home is a LEVEL on the home tile, upgraded in place.
// SHACK is the free default (the humble "rice store" model that used to be the
// granary, Tile_10); paying for the MIDDLE upgrade swaps it to the manor (Tile_7).
export const HomeLevels = Object.freeze({ SHACK: 'SHACK', MIDDLE: 'MIDDLE' })
export const HomeDioramaTile = Object.freeze({ SHACK: 10, MIDDLE: 7 })

// Synty low-poly recipes (public/models/capital/hex_recipes.json) — these REPLACE the Tile_N dioramas above
// whenever the Synty kit has loaded; the Tile_N numbers stay as the fallback. Edit the recipes, not code.
export const BuildingRecipe = Object.freeze({
  [BuildingTypes.SCHOOL]: 'SCHOOL', [BuildingTypes.HOSPITAL]: 'HOSPITAL', [BuildingTypes.TEMPLE]: 'TEMPLE',
  [BuildingTypes.SHOP]: 'SHOP', [BuildingTypes.GRANARY]: 'GRANARY', [BuildingTypes.WATCHTOWER]: 'WATCHTOWER',
})
export const HomeRecipe = Object.freeze({ SHACK: 'SHACK', MIDDLE: 'MANOR' })
export const FarmRecipe = 'PADDY'
export const GroundRecipe = 'GROUND'   // undeveloped land (plain tiles)
export const WeedsRecipe = 'WEEDS'     // bamboo forest (overgrown) — for a future "weeds" tile state

// CR-5: FARM tiles get a flooded rice-paddy diorama (Tile_18) so the field reads
// clearly as a paddy, not just a tinted hex.
export const FarmDioramaTile = 18

// Total building-stacked bonus ceilings — keeps an unlimited-build economy
// from running away. Bonuses sum across every standing building, then the
// total is clamped to these caps before being applied per click / per tick.
//   • goonRate: 100% total (effective +2× goon speed from buildings alone)
//   • yield:    50m flat per paw click from buildings alone
// Anything beyond the cap is wasted, so the player can keep building for
// visuals + flavor without breaking the rate curve.
export const BuildingBonusCap = Object.freeze({
  goonRate: 1.0,
  yield: 50,
})

// ZONING — if the player places a building on a plot type the building isn't
// allowed on, the kingdom's Emperor confiscates a fine and dispatches an
// insulting messenger (a narrative-beat overlay). The fine is a flat multiple
// of the building's cost, so taller/more-expensive missteps hurt proportionally.
export const ZoningConfig = Object.freeze({
  fineMultiplier: 10,
})

// Field dimensions. Phase 8: expanded to 12×12 so the player can wander
// beyond their starter plot and buy For Sale tiles to expand the kingdom.
export const Field = Object.freeze({
  gridSize: 12, // 12x12 tiles
  tileSize: 1,
  tileHeight: 0.1,
  // Uniform scale applied to every cat mesh (hero, peasants, wild goons).
  // Placeholder cats are ~1.5 world units tall on a 1.0-wide tile. Cats need
  // to read as small figures *next to* a building — buildings use a 0.65 tile
  // footprint, so at 0.1625 a cat is ~0.11 wide × 0.25 tall (~17% of a
  // building's footprint), giving the "tiny villager standing by a house"
  // proportion the player expects.
  catScale: 0.1625,
})

// PLOTS — per-tile ownership and land-type. Phase A renders existing types
// visually distinct; Phase B adds the purchase flow. Tile indices not present
// in profile.kingdoms[X].plots are considered "for sale" (unowned, grey).
export const PlotTypes = Object.freeze({
  WILD: 'WILD',     // unowned wilderness (bamboo) — claim it to expand your land
  HOME: 'HOME',
  GRASS: 'GRASS',   // owned, cleared land (dark green) — zone it, then build
  FARM: 'FARM',     // rice paddy — the seasonal clicker target
  COMMERCIAL: 'COMMERCIAL',
  RESIDENTIAL: 'RESIDENTIAL',
})

// Cost to CLAIM one unowned wild tile → owned GRASS you can then zone + build on.
export const LandConfig = Object.freeze({ claimCost: 50_000 }) // CR-5: a plain plot of land

// CR-9 land market — base price per ring from the home (center = ring 0). Prime
// land adjoining the home (ring 1) is dearest; the wild edges (ring 6) cheapest.
export const LandRingPrices = Object.freeze({
  1: 150_000, 2: 100_000, 3: 65_000, 4: 40_000, 5: 25_000, 6: 15_000,
})
// Supply & demand: unclaimed plots cost more as your field fills up. Final price
// = ringBase × (1 + slope × claimedFraction). slope 1.0 → the last plot ≈ 2× base.
export const LandDemandSlope = 1.0
// Zone an owned plot into a rice paddy (your main income land).
export const RicePaddyPrice = 5_000

// Rice economy — rice paddies are the player's main money engine. Each owned
// FARM tile multiplies the per-click SEASONAL yield, so the more paddies you
// grow + harvest, the bigger every click pays (strongest in HARVEST season).
// THE FIELD IS THE PROGRESS BAR — how the rice rows grow with the player's clicks (src/game/riceGrowth.js, scene/riceField.js).
// THE RHYTHM COMBO (game/combo.js): clicks within gapMs of each other build the combo a step a click (1 / steps) up to 1; the click
// value is × (1 + maxBonus × combo); once the window passes it fades to 0 over decayMs. The click sound climbs maxSemitones.
export const ComboConfig = Object.freeze({ gapMs: 1000, steps: 10, maxBonus: 0.5, decayMs: 1200, maxSemitones: 7 })
// THE 10-SECOND LOOP's feedback (scene/riceField.js, game/loopFeedback.js, ui/hud.js)
export const LoopFeedback = Object.freeze({
  bladeBump: 0.22,       // a notch makes the row's blades pop this much taller for a moment
  bumpDecayPerSec: 7,    // …and settle at this rate
  surplusTingEvery: 1,   // a gold ting every N clicks past a full field
  sweepStaggerMs: 35,    // the season-turn sweep: each row in fill order bumps this many ms after the one before
  floatCap: 14,          // at most this many "+Nm" numbers in flight (a fast streak would otherwise flood the page)
})
// THE MESSAGE RAIL (ui/messageRail.js): every message at bottom-centre, one at a time; a message holds at least minMs before a
// newer one bumps it down and away; plain messages leave after infoMs / storyMs; a choice left unmade comes back after reappearMs
// (a "Later" choice puts it off snoozeMs).
export const MessageRailConfig = Object.freeze({ minMs: 5000, infoMs: 9000, storyMs: 14000, eventMs: 12000, reappearMs: 45000, snoozeMs: 180000, slideMs: 350, tickMs: 200, staleMs: 30000 })
// (staleMs: a plain message that waited this long in the queue is dropped - the log has it - so a busy minute is not replayed for minutes)
// THE GOLDEN GLINT (game/glint.js): a glint on a row for windowMs; a paw click while it is up pays × boostMult for boostClicks.
export const GlintConfig = Object.freeze({ firstAfterMs: 20000, everyMinMs: 25000, everyMaxMs: 50000, windowMs: 3000, boostClicks: 5, boostMult: 3 })
// DIRECT A CITIZEN (scene/peasants.js directCitizen): click a citizen → it kneels on the row your next click fills — a notch, and
// payMult of a click's value; one such errand per citizen every cooldownMs, one for the whole village every globalGapMs. A filled row
// also calls the nearest idle citizen within chainReach to kneel on it (the row chain — for show, no coin).
export const DirectTendConfig = Object.freeze({ payMult: 0.5, cooldownMs: 10000, globalGapMs: 2000, chainReach: 2.5, giveUpMs: 14000 }) // giveUpMs: an errand that has not reached its kneel by then is dropped (never stuck)
// THE CROW (scene/crow.js): a shadow sweeps in and a crow lands on a row with rice on it (progress ≥ minRowProgress); after sitMs it
// eats eatsClicks of this season's clicks — click it first and it flies off.
export const CrowConfig = Object.freeze({ firstAfterMs: 45000, everyMinMs: 50000, everyMaxMs: 110000, sitMs: 6000, eatsClicks: 1, sweepMs: 1100, minRowProgress: 0.2 })
// THE TRIBUTE RAMP (game/tributeRamp.js, taxManager.js): the running tally idles at idlePerSec; while the player rapid-clicks the
// rules-based income (citizens, education, paddies, plots) ramps in over rampUpMs, and eases out over rampDownMs afterwards.
export const TributeRamp = Object.freeze({ idlePerSec: 1, rampUpMs: 4000, rampDownMs: 8000 })
// THE ACTIVE FIELD RIM (scene/riceField.js): while the player clicks, the hex of the paddy holding the row the next click fills glows
// softly along its edge — the field being maintained. It follows the fill order from paddy to paddy (the old rim fades as the new one
// rises) and fades out holdMs after the clicking stops.
export const ActiveField = Object.freeze({ holdMs: 3000, width: 0.05, color: 0xffe08a, fadeIn: 7, fadeOut: 2.5, opacity: 0.55 })
export const RiceGrowth = Object.freeze({
  clicksPerPaddy: 25,   // clicks that fill a season for one paddy (5 rows → 5 clicks a row); the count under the clicker shows "n / target rice"
  stubbleScale: 0.2,    // the rows' height right after the harvest (before PREP starts them again)
  sparkMs: 420,         // a click's spark runs the length of its row in this long
  sparksPerSec: 12,     // sparks beyond this rate are skipped (the clicks still count)
  tendFxPerSec: 20,     // citizens' tending puffs, all citizens together
})

export const RiceConfig = Object.freeze({
  clickScalePerTile: 0.12, // +12% per-click seasonal yield per owned rice paddy
  passivePerTile: 1,       // m/sec passive "the rice grows itself" trickle per paddy (idle reward)
})

// School — educates citizens who wander near it. An educated citizen earns more
// passive Mao Coin (higher "collection power"). Education is permanent + capped
// at the number of citizens you own.
export const SchoolConfig = Object.freeze({
  educateRadius: 1.8,    // a citizen this close (world units) to a School graduates
  tributeBonusEach: 0.5, // +m/sec active income per educated citizen (FIELDS mode only; counts while rapid-clicking — see TributeRamp)
})

// Work Mode — the core strategic dial. FIELDS = workers tend the rice (full
// passive income + school bonus) but the village is undefended; VILLAGE =
// workers guard the buildings (income drops, no school bonus) but goons can't
// burn. Per-kingdom; default FIELDS.
export const WorkModes = Object.freeze({ FIELDS: 'FIELDS', VILLAGE: 'VILLAGE' })
export const WorkModeConfig = Object.freeze({
  villageWageMult: 0.5, // citizen wages multiplier while guarding the village
  villageRiceMult: 0.5, // rice-paddy passive multiplier while fields go untended
})

// Two citizens (or a citizen and your cat) that bump into each other stop and stand idle for this long, and only THEN turn and walk
// away — no sudden snap into the other direction (scene/peasants.js).
export const CollisionConfig = Object.freeze({ pauseMs: 1000 })

// REST / WORK — the citizens' shift (game/restManager.js, scene/peasants.js). REST sends every citizen to the nearest building
// to sleep; WORK (or the timer) sends them back out. 1 real minute = 1 game hour, so the default 8-minute rest is 8 game hours.
export const RestConfig = Object.freeze({
  durationMs: 8 * 60_000,   // they go back to work by themselves after this long
  gameHourMs: 60_000,       // 1 minute = 1 game hour
  pausesWork: true,         // while resting they earn no wages and add nothing to clicks (set false for a purely visual rest)
  enterDist: 0.55,          // world units from a building's centre where a citizen starts fading as it walks in
  walkSecPerUnit: 1.0,      // how briskly they retreat (seconds per world unit)
  fadeMs: 600,              // fade out / fade in
})

// (Injury: a citizen that brawls a goon survives but is hurt — it contributes NOTHING to wages or clicks until healed by Feed, a
//  Hospital or a Devout cat. That rule lives in seasonManager.getClickValue and taxManager.getRate; there is no separate setting.)

// Hospital — an injured citizen that wanders within range recovers to full
// health (walk-by, just like a School educates).
export const HospitalConfig = Object.freeze({
  healRadius: 1.8,
  stayMs: 48 * 60 * 60 * 1000, // a patient not healed within this dies (the clock runs while you are away — "a day or two")
  healCostMult: 10,            // healing IN the hospital costs this many times the FEED rate per citizen (ancient health care is dear; so is neglect)
  reminderEveryMs: 5 * 60 * 1000, // the scribe reminds you about patients this often
  // A patient worsens the longer it waits: its CONDITION follows how far through the stay it is (`untilFrac` = the share of stayMs at
  // which the next condition begins). Treating a patient costs feed rate × healCostMult × costMult — neglect is dear, and the worse
  // the dearer. TERMINAL (`fatal`) patients cannot be saved: treatment is charged in full and the citizen dies (a headstone).
  conditions: [
    { id: 'mild', label: 'Mild', untilFrac: 0.25, costMult: 1 },
    { id: 'serious', label: 'Serious', untilFrac: 0.5, costMult: 3 },
    { id: 'severe', label: 'Severe', untilFrac: 0.8, costMult: 10 },
    { id: 'terminal', label: 'Terminal', untilFrac: 1.01, costMult: 25, fatal: true },
  ],
})

// Positive tool (key 2) — hover a citizen to freeze it and pay to better them
// on the spot, instead of waiting for them to wander past a building:
//   • School   → graduate now (earns more + immune to turning feral)
//   • Temple   → BLESS → Devout (immune to feral AND mends other citizens on
//                contact)
//   • Hospital → heal an injured citizen instantly
export const PositiveToolConfig = Object.freeze({
  schoolFee: 400,
  templeFee: 800,
  hospitalFee: 250,
  devoutHealRadius: 0.45, // a Devout citizen mends any injured cat it bumps into
})

// HOME CARE — a kingdom with no School or Hospital is not helpless, it just pays for the privilege: "home schooling" and "home
// healthcare" cost these multiples of the building's rate (the key-2 fees above, and the Hospital's treatment prices). Owning the
// building drops the price to the base rate (and a School/Hospital also works on its own as citizens wander past) — the game says so
// at the moment you pay, so the first big bill is the nudge to build.
export const HomeCare = Object.freeze({
  schoolMult: 3,   // home schooling: 3× schoolFee until a School is built
  healthMult: 2.5, // home healthcare: 2.5× every treatment price (key-2 heal and the Hospital pop-up) until a Hospital is built
})

// Feral turning — the next generation gone to seed. On a slow ticker an
// untended citizen ("the children") can turn feral: the player LOSES that
// loyal cat and a hostile wild goon spawns in its place, ready to torch the
// village. The School is the shield — an EDUCATED citizen (mortarboard) is
// immune, as is a Temple-blessed DEVOUT citizen (the positive tool, later).
// So the strategic pull is: school/bless your people or they turn on you.
export const FeralConfig = Object.freeze({
  checkIntervalMs: 13000, // how often we roll a feral turn
  chancePerCheck: 0.22,   // probability one eligible citizen turns feral per roll
  minCitizens: 3,         // never turn feral below this headcount (don't gut a tiny village)
  restlessMs: 90_000,     // the roll first makes a citizen RESTLESS (a purple mark over its head, a warning): it turns feral only if it is
                          // still unschooled and unblessed after this long — time to school it (press 2 / a School) or bless it
  maxRestless: 3,         // at most this many restless citizens at once
})

// The green ground. Owned GRASS is a dark cultivated green; unowned WILD is a
// brighter untamed green carpeted in bamboo. treeChance scatters trees on owned
// grass for life.
export const GrassConfig = Object.freeze({
  color: 0x8a9a4e,       // undeveloped-land olive (matches the GROUND recipe)
  treeChance: 0.18,      // fraction of owned grass tiles that spawn a tree
})

export const PlotConfig = Object.freeze({
  [PlotTypes.WILD]: {
    id: 'WILD', name: 'Wild Land', color: 0x5f7f3a, // untamed forest green (bamboo forest on top)
    cost: 0, // not "bought" as a type — you CLAIM it (LandConfig.claimCost → GRASS)
    desc: 'Unclaimed wilderness. Claim it to expand your kingdom.',
  },
  [PlotTypes.HOME]: {
    id: 'HOME', name: 'Home', color: 0x8b3a3a, // red roof
    cost: 0, // starter, not purchasable
    desc: 'Where you live. Hack-to-home returns here.',
  },
  [PlotTypes.GRASS]: {
    id: 'GRASS', name: 'Grassland', color: 0x8a9a4e, // cleared, owned land (open olive ground)
    cost: 0,
    desc: 'Cleared, owned land. Zone it (rice / commercial / residential), then build.',
  },
  [PlotTypes.FARM]: {
    id: 'FARM', name: 'Rice Paddy', color: 0x8b6f3d, // brown — base PREP color
    cost: 5_000,
    desc: 'Rice paddy. Cycles through seasons. Earns Mao Coin on harvest.',
  },
  [PlotTypes.COMMERCIAL]: {
    id: 'COMMERCIAL', name: 'Commercial', color: 0x9c7a4a, // tan
    cost: 25_000,
    desc: 'Passive +5m/sec tribute per plot.',
  },
  [PlotTypes.RESIDENTIAL]: {
    id: 'RESIDENTIAL', name: 'Residential', color: 0x6e7a5a, // muted
    cost: 50_000,
    desc: 'Future: marriage / birth / family.',
  },
})

// For Sale (unowned) tile visual.
export const ForSaleConfig = Object.freeze({
  tileColor: 0x3d3d3d, // grey base
  signColor: 0xe0a040, // gold sign
  signHeight: 0.22, // total post height — tiny by default, tooltip on hover
})

// Commercial plot passive tribute. Every COMMERCIAL plot pays this many
// Mao Coin per second, on top of goon-based tribute. Flows through taxManager
// so the same federation / event / title multipliers apply.
export const CommercialPlotTribute = 5

// localStorage key namespace. Bump suffix when save schema changes.
export const SaveKey = 'mao-kingdom/save/v9'

// THREE KINGDOMS — separate save buckets per kingdom; switch via top tabs.
// Each kingdom has its own wealth, goons, buildings, season. Only the active
// one ticks; tribute catches up via lastTaxTick when you switch back.
// Wei / Han / Wu match the Mao Kingdom Flow doc. "Han" is the dynasty Liu Bei
// claimed to continue — historians often call his kingdom "Shu Han" so the
// narrative beats keep him as the central figure of this kingdom.
export const KingdomIds = Object.freeze({
  WEI: 'WEI',
  HAN: 'HAN',
  WU: 'WU',
})

export const KingdomOrder = [KingdomIds.WEI, KingdomIds.HAN, KingdomIds.WU]

// FEDERATION — multiplier applied to all paw yields and tax tribute based on
// how many kingdoms you've unlocked. Late-game incentive to play all three.
export const FederationMultipliers = Object.freeze({
  1: 1.0,
  2: 1.1,
  3: 1.25,
})

// HOLIDAYS — limited-time celebration windows. Each entry has a `family`
// (the trophy line — e.g. all Lunar New Years share family='lunar') plus a
// `token` unique to the specific year (used by the server's holidaysSeen
// stamping so re-participating doesn't double-fire the toast). Date ranges
// are UTC YYYY-MM-DD strings, inclusive. Lunar/lunisolar dates shift each
// year, so we maintain them by hand here.
//
// Active during the window:
//   • clickMultiplier — multiplies per-click yield
//   • streakBonusMult — multiplies streak milestone rewards
//   • label / emoji rendered in the HUD banner + toast
// Daily login streak — the bonus paid when the streak reaches each day count (server/index.js pays it; the Game Guide reads it).
export const StreakRewards = Object.freeze({ 2: 50, 3: 200, 7: 2_000, 14: 10_000, 30: 100_000 })

export const HolidayCalendar = Object.freeze([
  // ── Lunar New Year ───────────────────────────────────────────────────────
  { token: 'lunar_2026', family: 'lunar', label: 'Lunar New Year — Year of the Horse', emoji: '🧧',
    start: '2026-02-17', end: '2026-02-23', clickMultiplier: 1.5, streakBonusMult: 2.0 },
  { token: 'lunar_2027', family: 'lunar', label: 'Lunar New Year — Year of the Goat',  emoji: '🧧',
    start: '2027-02-06', end: '2027-02-12', clickMultiplier: 1.5, streakBonusMult: 2.0 },

  // ── Mid-Autumn Festival ──────────────────────────────────────────────────
  { token: 'midautumn_2026', family: 'midautumn', label: 'Mid-Autumn — Moon Festival', emoji: '🌕',
    start: '2026-09-25', end: '2026-09-27', clickMultiplier: 1.3, streakBonusMult: 1.5 },
  { token: 'midautumn_2027', family: 'midautumn', label: 'Mid-Autumn — Moon Festival', emoji: '🌕',
    start: '2027-09-15', end: '2027-09-17', clickMultiplier: 1.3, streakBonusMult: 1.5 },

  // ── Dragon Boat Festival ─────────────────────────────────────────────────
  { token: 'dragonboat_2026', family: 'dragonboat', label: 'Dragon Boat Festival', emoji: '🐉',
    start: '2026-06-19', end: '2026-06-21', clickMultiplier: 1.3, streakBonusMult: 1.5 },
  { token: 'dragonboat_2027', family: 'dragonboat', label: 'Dragon Boat Festival', emoji: '🐉',
    start: '2027-06-09', end: '2027-06-11', clickMultiplier: 1.3, streakBonusMult: 1.5 },

  // ── Gregorian New Year ───────────────────────────────────────────────────
  { token: 'newyear_2027', family: 'newyear', label: 'Western New Year — Auld Lang Syne', emoji: '🎆',
    start: '2026-12-31', end: '2027-01-02', clickMultiplier: 1.2, streakBonusMult: 1.5 },
  { token: 'newyear_2028', family: 'newyear', label: 'Western New Year — Auld Lang Syne', emoji: '🎆',
    start: '2027-12-31', end: '2028-01-02', clickMultiplier: 1.2, streakBonusMult: 1.5 },
])

// Helper — find the holiday active on `utcDay` ('YYYY-MM-DD'). Returns null
// if none. If multiple overlap, returns the FIRST in calendar order.
export function activeHolidayFor(utcDay) {
  if (!utcDay) return null
  for (const h of HolidayCalendar) {
    if (utcDay >= h.start && utcDay <= h.end) return h
  }
  return null
}

// ADVENTURES — the cat embarks on a quest into a different biome. Discovery
// rolls + scribe messages + home risk. Each adventure has:
//   • duration      — real-time minutes (server-deterministic clock used)
//   • biome         — picks the scene/palette/backdrop. v1 just colors the
//                     adventure panel; v2 will swap the voxel scene
//   • discoveryTable — array of { item, weight, minCoin, maxCoin } — rolled
//                     once per "discovery tick" while in DISCOVERY mode
//   • discoveryTickSec — seconds between discovery rolls (DISCOVERY mode)
//   • dangerProfile  — { feedDrainMult, raidChancePerMin, raidSeverityMult }
//                      — applied to home while the cat is away. DEFENSE mode
//                      halves raidSeverityMult; DISCOVERY mode does nothing
//                      to it.
//   • scribeBeats   — array of `{ atSec, kind, message }` — pre-scripted
//                     messages from the home scribe. kind drives the icon
//                     (pigeon, smoke, courier).
export const AdventureModes = Object.freeze({
  DISCOVERY: 'DISCOVERY', // boosts discovery rolls, no defense help
  DEFENSE:   'DEFENSE',   // halves raid severity, no discovery rolls
  NEUTRAL:   'NEUTRAL',   // reticle off — no discovery rolls, no defense bonus
})

// Early-exit economics. Two ways to come home before the timer:
//   • Recall  — a measured retreat. Cash out discovered coins at a haircut and
//     KEEP the relics found this trip. No completion achievement.
//   • Abandon — a panic sprint home (e.g. home is under raid). FORFEIT every
//     coin AND relic found this trip, and pay a flat "scramble fine" from the
//     treasury (tribute/supplies dropped in the rush). The trade is speed: the
//     cat is home instantly to defend.
export const AdventureRules = Object.freeze({
  recallPayoutPct: 0.7, // recall pays 70% of discovered coins
})

export const AdventureConfig = Object.freeze([
  {
    id: 'ADV_FOREST',
    name: 'Forest Pilgrimage',
    biome: 'forest',
    backdrop: '#1d3a26',       // dim green panel tint
    duration: 90,              // 1.5 min — quick scout
    discoveryTickSec: 12,
    abandonFine: 150,          // flat treasury fine for a panic sprint home
    dangerProfile: { feedDrainMult: 1.3, raidChancePerMin: 0.03, raidSeverityMult: 0.6 },
    discoveryTable: [
      { item: 'Wild herbs',       weight: 5, minCoin: 80,   maxCoin: 220 },
      { item: 'Lost coins',       weight: 3, minCoin: 150,  maxCoin: 400 },
      { item: 'Forager satchel',  weight: 2, minCoin: 400,  maxCoin: 800 },
      { item: 'Carved jade idol', weight: 1, minCoin: 1200, maxCoin: 2200 },
    ],
    // Check-in schedule only — the scribe's TEXT is built from the real home state when each fires (adventureManager).
    scribeBeats: [
      { atSec: 20, kind: 'pigeon' },
      { atSec: 45, kind: 'pigeon' },
      { atSec: 75, kind: 'pigeon' },
    ],
  },
  {
    id: 'ADV_MOUNTAIN',
    name: 'Mountain Expedition',
    biome: 'mountain',
    backdrop: '#3d3328',       // stone-grey/brown tint
    duration: 360,             // 6 min — meaningful expedition
    discoveryTickSec: 20,
    abandonFine: 1500,
    dangerProfile: { feedDrainMult: 1.6, raidChancePerMin: 0.05, raidSeverityMult: 0.9 },
    discoveryTable: [
      { item: 'Mountain ore',       weight: 5, minCoin: 250,  maxCoin: 700 },
      { item: 'Ancient bronze',     weight: 3, minCoin: 800,  maxCoin: 2000 },
      { item: 'Sage’s scroll', weight: 2, minCoin: 2500, maxCoin: 5000 },
      { item: 'Jade dragon relic',  weight: 1, minCoin: 8000, maxCoin: 15000 },
    ],
    // Check-in schedule only — the scribe's TEXT is built from the real home state when each fires (adventureManager).
    scribeBeats: [
      { atSec: 30,  kind: 'pigeon' },
      { atSec: 90,  kind: 'smoke' },
      { atSec: 180, kind: 'pigeon' },
      { atSec: 270, kind: 'pigeon' },
      { atSec: 330, kind: 'pigeon' },
    ],
  },
  {
    id: 'ADV_COAST',
    name: 'Coastal Patrol',
    biome: 'coast',
    backdrop: '#163046',       // deep ocean blue tint
    duration: 720,             // 12 min — the long voyage
    discoveryTickSec: 30,
    abandonFine: 6000,
    dangerProfile: { feedDrainMult: 2.0, raidChancePerMin: 0.07, raidSeverityMult: 1.2 },
    discoveryTable: [
      { item: 'Sea pearls',          weight: 5, minCoin: 600,   maxCoin: 1400 },
      { item: 'Foreign silks',       weight: 3, minCoin: 2000,  maxCoin: 4500 },
      { item: 'Dragon-king’s coin', weight: 2, minCoin: 6000,  maxCoin: 12000 },
      { item: 'Bronze nautical chart',  weight: 1, minCoin: 18000, maxCoin: 35000 },
    ],
    // Check-in schedule only — the scribe's TEXT is built from the real home state when each fires (adventureManager).
    scribeBeats: [
      { atSec: 60,  kind: 'pigeon' },
      { atSec: 180, kind: 'pigeon' },
      { atSec: 330, kind: 'pigeon' },
      { atSec: 480, kind: 'pigeon' },
      { atSec: 630, kind: 'pigeon' },
    ],
  },
])

// ACHIEVEMENTS — milestone badges earned through play. Triggers come in
// four flavors:
//   • count   — sum of an event hitting `threshold` (e.g. WildGoonSlain × 100)
//   • profile — a profile-field crossing `threshold` (e.g. profile.children.length ≥ 5)
//   • kingdom — an active-kingdom-field crossing (e.g. kingdom.wealth ≥ 10M)
//   • first   — server-awarded "first to do X" trophy (only one user gets it)
// Rarity drives the badge color in the Registry's ACHIEVEMENTS section.
export const AchievementRarity = Object.freeze({
  common:    { label: 'Common',    color: '#9aa0a6' },
  rare:      { label: 'Rare',      color: '#6c8aa8' },
  epic:      { label: 'Epic',      color: '#c9b8ff' },
  legendary: { label: 'Legendary', color: '#ecc06b' },
})

export const AchievementConfig = Object.freeze([
  // Combat — slaying wild goons
  { id: 'first_blood',    rarity: 'common',    name: 'Bloodied Paws',       desc: 'Slay your first wild goon.',
    trigger: { kind: 'count', event: 'WildGoonSlain', threshold: 1 } },
  { id: 'goon_hunter',    rarity: 'rare',      name: 'Goon Hunter',         desc: 'Slay 100 wild goons.',
    trigger: { kind: 'count', event: 'WildGoonSlain', threshold: 100 } },
  { id: 'goon_butcher',   rarity: 'epic',      name: 'Goon Butcher',        desc: 'Slay 500 wild goons.',
    trigger: { kind: 'count', event: 'WildGoonSlain', threshold: 500 } },

  // Industry — clicks + buildings
  { id: 'first_click',    rarity: 'common',    name: 'A Long Journey',      desc: 'Tend the field once.',
    trigger: { kind: 'kingdom', field: 'clickCount', threshold: 1 } },
  { id: 'click_thousand', rarity: 'rare',      name: 'Calloused Paws',      desc: 'Reach 1,000 lifetime clicks.',
    trigger: { kind: 'kingdom', field: 'clickCount', threshold: 1_000 } },
  { id: 'click_ten_k',    rarity: 'epic',      name: 'Tireless Toil',       desc: 'Reach 10,000 lifetime clicks.',
    trigger: { kind: 'kingdom', field: 'clickCount', threshold: 10_000 } },
  { id: 'first_building', rarity: 'common',    name: 'Skyline',             desc: 'Erect your first structure.',
    trigger: { kind: 'count', event: 'BuildingAdded', threshold: 1 } },
  { id: 'urbanist',       rarity: 'rare',      name: 'Urbanist',            desc: 'Erect 10 structures.',
    trigger: { kind: 'count', event: 'BuildingAdded', threshold: 10 } },

  // Wealth ladder
  { id: 'first_million',  rarity: 'rare',      name: 'First Million',       desc: 'Accumulate 1,000,000m.',
    trigger: { kind: 'kingdom', field: 'wealth', threshold: 1_000_000 } },
  { id: 'first_ten_mil',  rarity: 'epic',      name: 'Ten Million Strong',  desc: 'Accumulate 10,000,000m.',
    trigger: { kind: 'kingdom', field: 'wealth', threshold: 10_000_000 } },

  // Family — marriages + children
  { id: 'open_heart',     rarity: 'common',    name: 'Open Heart',          desc: 'Mark yourself open to marriage.',
    trigger: { kind: 'profile', field: 'openToMarriage', threshold: true } },
  { id: 'wed',            rarity: 'rare',      name: 'Wed',                 desc: 'Marry your first spouse.',
    trigger: { kind: 'count', event: 'MarriageAccepted', threshold: 1 } },
  { id: 'polygamist',     rarity: 'epic',      name: 'Many Partners',       desc: 'Have 5 active marriages.',
    trigger: { kind: 'activeMarriages', threshold: 5 } },
  { id: 'first_child',    rarity: 'common',    name: 'A New Heir',          desc: 'Welcome your first kitten.',
    trigger: { kind: 'count', event: 'ChildBorn', threshold: 1 } },
  { id: 'dynasty',        rarity: 'epic',      name: 'Dynasty',             desc: 'Father a brood of 5 children.',
    trigger: { kind: 'count', event: 'ChildBorn', threshold: 5 } },

  // Tier — Kingdom Club
  { id: 'kingdom_club',   rarity: 'rare',      name: 'Kingdom Club',        desc: 'Subscribe to Kingdom Club.',
    trigger: { kind: 'membership', tier: 'KINGDOM_CLUB' } },
  // Server-awarded — the FIRST player ever to subscribe holds this legendary trophy.
  { id: 'first_kc_member',rarity: 'legendary', name: 'Inaugural Member',    desc: 'First citizen to join Kingdom Club.',
    trigger: { kind: 'first', token: 'kingdom_club_member' } },

  // Caretaker — feeding + raid resilience
  { id: 'gentle_caretaker', rarity: 'common',  name: 'Gentle Caretaker',    desc: 'Feed your citizens for the first time.',
    trigger: { kind: 'count', event: 'FeedActivated', threshold: 1 } },
  { id: 'survived_raid',  rarity: 'rare',      name: 'Held the Line',       desc: 'Survive a bandit raid (raid ended).',
    trigger: { kind: 'count', event: 'RaidEnd', threshold: 1 } },

  // Adventures — risk reward in the wilds
  { id: 'adv_first',     rarity: 'common', name: 'First Steps',          desc: 'Complete your first adventure.',
    trigger: { kind: 'count', event: 'AdventureCompleted', threshold: 1 } },
  { id: 'adv_wanderer',  rarity: 'rare',   name: 'Wanderer',             desc: 'Complete 10 adventures.',
    trigger: { kind: 'count', event: 'AdventureCompleted', threshold: 10 } },
  { id: 'adv_explorer',  rarity: 'epic',   name: 'Far-Roaming Explorer', desc: 'Complete 50 adventures.',
    trigger: { kind: 'count', event: 'AdventureCompleted', threshold: 50 } },
  { id: 'adv_all_biomes',rarity: 'legendary', name: 'All Three Biomes',  desc: 'Finish a Forest, a Mountain, and a Coastal adventure.',
    trigger: { kind: 'adventureBiomes', biomes: ['forest', 'mountain', 'coast'] } },

  // Holidays — limited-time trophies for being a citizen during the festival
  // window. Server stamps `holidaysSeen[token]` once you auth during the
  // window; client mirrors that into the local achievements ledger.
  { id: 'holiday_lunar',     rarity: 'rare',  name: 'Lunar Reveler',       desc: 'Welcome a Lunar New Year in the kingdom.',
    trigger: { kind: 'holiday', family: 'lunar' } },
  { id: 'holiday_midautumn', rarity: 'rare',  name: 'Moon Watcher',        desc: 'Honor the Mid-Autumn moon with the kingdom.',
    trigger: { kind: 'holiday', family: 'midautumn' } },
  { id: 'holiday_dragonboat',rarity: 'rare',  name: 'Dragon Boat Racer',   desc: 'Race the dragon boats with the kingdom.',
    trigger: { kind: 'holiday', family: 'dragonboat' } },
  { id: 'holiday_newyear',   rarity: 'common',name: 'Auld Lang Syne',      desc: 'Toast the new Gregorian year with the kingdom.',
    trigger: { kind: 'holiday', family: 'newyear' } },

  // Devotion — daily login streaks (server-tracked, UTC day rollover)
  { id: 'streak_3',       rarity: 'common',    name: 'Returning Cat',       desc: 'Log in 3 days in a row.',
    trigger: { kind: 'streak', threshold: 3 } },
  { id: 'streak_7',       rarity: 'rare',      name: 'Devoted',             desc: 'Log in 7 days in a row.',
    trigger: { kind: 'streak', threshold: 7 } },
  { id: 'streak_30',      rarity: 'epic',      name: 'Loyal Citizen',       desc: 'Log in 30 days in a row.',
    trigger: { kind: 'streak', threshold: 30 } },
])

// NARRATIVE — wealth milestones unlock short story beats tied to historical
// figures from each kingdom. Each beat fires once per kingdom (tracked in
// save state as `seenBeats`).
export const NarrativeBeats = Object.freeze({
  WEI: [
    {
      id: 'wei-1',
      threshold: 500,
      title: 'Cao Cao 曹操',
      body: 'Cao Cao watches the field. "A man who feeds many cats," he says, "rules many cats."',
    },
    {
      id: 'wei-2',
      threshold: 5_000,
      title: 'The Granary',
      body: 'The general orders new granaries built. "Hunger is the enemy of empire," he tells his scribes.',
    },
    {
      id: 'wei-3',
      threshold: 50_000,
      title: 'The Feast',
      body: 'Cao Cao smiles in the lamplight. "Wei prospers. Now we feast — and then we march."',
    },
  ],
  HAN: [
    {
      id: 'han-1',
      threshold: 500,
      title: 'Liu Bei 劉備',
      body: 'Liu Bei kneels in the rice. "These small lives," he murmurs to the kittens, "are the soul of the Han."',
    },
    {
      id: 'han-2',
      threshold: 5_000,
      title: 'Zhuge Liang 諸葛亮',
      body: 'Zhuge Liang reads the night auguries. "Education will save your people, my lord. Sharpen the pen before the sword."',
    },
    {
      id: 'han-3',
      threshold: 50_000,
      title: 'The Mountain Kingdom',
      body: 'Liu Bei weeps quietly into his robes. "A kingdom of cats, all my own — and yet, never enough."',
    },
  ],
  WU: [
    {
      id: 'wu-1',
      threshold: 500,
      title: 'Sun Quan 孫權',
      body: 'Sun Quan walks the riverbank. The kittens bat at his sleeve. "The Yangtze runs both ways."',
    },
    {
      id: 'wu-2',
      threshold: 5_000,
      title: 'Zhou Yu 周瑜',
      body: 'Zhou Yu plots over wine. "Three kingdoms. Three fires. Which shall we kindle first?"',
    },
    {
      id: 'wu-3',
      threshold: 50_000,
      title: 'The River',
      body: 'Sun Quan looks east, then south. "Wu does not conquer," he says. "Wu endures."',
    },
  ],
})

// EVENTS — rolled at each SeasonChange. Drought / Bandits / Festival.
export const EventTypes = Object.freeze({
  DROUGHT: 'DROUGHT',
  BANDITS: 'BANDITS',
  FESTIVAL: 'FESTIVAL',
})

export const EventConfig = Object.freeze({
  rollChance: 0.3, // 30% chance of any event on season change
  table: [
    { type: EventTypes.DROUGHT, weight: 1, label: 'Drought', icon: '☼', durationMs: 60_000, yieldMult: 0.5, rateMult: 1.0, instantWealthMult: 1.0, desc: 'Yields halved.', action: 'Your passive tribute is halved for 60 s. Nothing fixes it; it ends by itself. Your clicks still earn full coin, so keep clicking.' },
    { type: EventTypes.BANDITS, weight: 1, label: 'Bandits', icon: '⚔', durationMs: 0, yieldMult: 1.0, rateMult: 1.0, instantWealthMult: 0.9, desc: 'Lost 10% wealth.', action: 'Nothing to do now. Keep clicking to earn it back.' },
    { type: EventTypes.FESTIVAL, weight: 1, label: 'Festival', icon: '✦', durationMs: 60_000, yieldMult: 1.5, rateMult: 1.5, instantWealthMult: 1.0, desc: 'Yields & goon rate +50%.', action: 'Passive tribute and citizen output are +50% for 60 s. Nothing to do; enjoy it.' },
  ],
})

export const KingdomConfig = Object.freeze({
  [KingdomIds.WEI]: {
    id: KingdomIds.WEI,
    name: 'WEI',
    subtitle: '魏',
    unlockCost: 0,
    accent: '#d4a548',
    palette: { PREP: 0x8b6f3d, PLANT: 0xc9a876, MAINTAIN: 0x4f7942, HARVEST: 0xd4a548 },
  },
  [KingdomIds.HAN]: {
    id: KingdomIds.HAN,
    name: 'HAN',
    subtitle: '漢',
    unlockCost: 10_000,
    accent: '#7aa86c',
    palette: { PREP: 0x4a5e3a, PLANT: 0x8a9c5c, MAINTAIN: 0x2f5638, HARVEST: 0xb8c95c },
  },
  [KingdomIds.WU]: {
    id: KingdomIds.WU,
    name: 'WU',
    subtitle: '吳',
    unlockCost: 100_000,
    accent: '#6c8aa8',
    palette: { PREP: 0x3a4a5e, PLANT: 0x6f8aa5, MAINTAIN: 0x2f4660, HARVEST: 0x4a90bd },
  },
})

// ── ADOPTION ROLES ──────────────────────────────────────────────────────────
// The role a player picks at signup. Three FREE Common-class roles (pick a
// playstyle, no head start) and three PAID tiers that grant a one-time
// head-start `startingPackage` (treasury + citizens + a pre-built kingdom) and
// a stronger title multiplier. Prices are real USD (one-time, via Stripe —
// wired in a later phase). Packages escalate cleanly on every axis so paying
// always buys a meaningfully bigger leg up, while liquid `wealth` stays below
// the dollar peg ($1 ≈ 1,000,000m) so the economy isn't broken.
//
// `startingPackage.buildings` lists building TYPES; game/startingPackage.js
// zones the correctly-typed tile for each (Temple→home, Granary→a rice paddy,
// School/Hospital/Watchtower→fresh COMMERCIAL tiles) per BuildingConfig
// allowedPlotTypes. Free roles have no package (default starter: home + 1 rice).
export const Roles = Object.freeze({
  FARMER: {
    id: 'FARMER', label: 'Farmer', priceUsd: 0, title: TitleTypes.FARMER, classId: ClassIds.COMMON,
    blurb: 'Work the rice paddies — +20% click yield. The honest grind to court.',
  },
  ARTISAN: {
    id: 'ARTISAN', label: 'Artisan', priceUsd: 0, title: TitleTypes.ARTISAN, classId: ClassIds.COMMON,
    blurb: 'Craft and build — +25% building yield. Climb through industry.',
  },
  MERCHANT: {
    id: 'MERCHANT', label: 'Merchant', priceUsd: 0, title: TitleTypes.MERCHANT, classId: ClassIds.COMMON,
    blurb: 'Trade and tax — +15% tax take. Profit your way up.',
  },
  CLERGY: {
    id: 'CLERGY', label: 'Clergy', priceUsd: 10, title: TitleTypes.CLERGY, classId: ClassIds.MIDDLE,
    tagline: 'Blessed Foundation',
    blurb: 'A temple, a granary, faithful citizens, and a starting purse. +10% to all yields.',
    startingPackage: {
      wealth: 5_000_000,
      goons: { [GoonTypes.PEASANT_CAT]: 3, [GoonTypes.SCRIBE_CAT]: 2 },
      buildings: [BuildingTypes.TEMPLE, BuildingTypes.GRANARY],
      extraFarms: 0, extraResidential: 0,
    },
  },
  NOBLEMAN: {
    id: 'NOBLEMAN', label: 'Nobleman', priceUsd: 15, title: TitleTypes.NOBLE, classId: ClassIds.HIGH,
    tagline: 'Noble Estate',
    blurb: 'A schooled estate where every citizen earns faster, plus land to expand. +25% to all yields.',
    startingPackage: {
      wealth: 8_000_000,
      goons: { [GoonTypes.PEASANT_CAT]: 5, [GoonTypes.SCRIBE_CAT]: 4 },
      buildings: [BuildingTypes.TEMPLE, BuildingTypes.GRANARY, BuildingTypes.SCHOOL],
      extraFarms: 0, extraResidential: 1,
    },
  },
  GENERAL: {
    id: 'GENERAL', label: 'Military General', priceUsd: 20, title: TitleTypes.MILITARY, classId: ClassIds.MIDDLE,
    tagline: "Warlord's Domain",
    blurb: 'A defended, developed domain — watchtower, hospital, school — the largest treasury and citizenry. +50% citizen income, +10% click.',
    startingPackage: {
      wealth: 12_000_000,
      goons: { [GoonTypes.PEASANT_CAT]: 8, [GoonTypes.SCRIBE_CAT]: 6 },
      buildings: [BuildingTypes.TEMPLE, BuildingTypes.GRANARY, BuildingTypes.SCHOOL, BuildingTypes.HOSPITAL, BuildingTypes.WATCHTOWER],
      extraFarms: 1, extraResidential: 1,
    },
  },
})

// Display order of the role cards (free first, then the paid tiers).
export const RoleOrder = ['FARMER', 'ARTISAN', 'MERCHANT', 'CLERGY', 'NOBLEMAN', 'GENERAL']

// ════════════════════════════════════════════════════════════════════════════
// COMMERCE ENGINE v2 — see docs/COMMERCE_MATRIX.md for the full equation set.
// Single source of truth for the rewritten wealth model. Added ADDITIVELY in
// CR-1: nothing consumes these yet, so the running game is unchanged until the
// later phases (CR-2+) switch each subsystem over and retire the old configs
// noted as RETIRE below. Numbers marked (tunable) are first-pass balance picks.
// ════════════════════════════════════════════════════════════════════════════

// ── Clicker economy ─────────────────────────────────────────────────────────
// clickValue = (maoBase + phdBonus·#PhDs) + perPeasant·Pₐ + perScribe·Sₐ , then
// ×fieldsMultiplier when work mode = FIELDS. Injured citizens contribute 0.
// Idle (no clicking) accrues per second at the idle rates (half the click rate).
export const ClickEconomy = Object.freeze({
  maoBase: 5,            // coins per Mao click (base)
  // V1.18 (Oct 2026): the four per-citizen numbers below were HALVED — the user found the income curve rose too fast. The base
  // click, the harvest (RiceValue × clicks) and the per-plot tribute are unchanged; only what each citizen adds is slower.
  perPeasant: 1,         // +coins/click per (healthy) peasant owned (was 2)
  perScribe: 2,          // +coins/click per (healthy) scribe owned (was 4)
  phdBonus: 1,           // +maoBase per PhD held, forever
  fieldsMultiplier: 2,   // ×click value while citizens are sent to the FIELDS
  peasantIdlePerSec: 0.5, // passive coins/sec per peasant when NOT clicking (was 1)
  scribeIdlePerSec: 1,   // passive coins/sec per scribe when NOT clicking (was 2)
})

// ── Citizens (peasants + scribes) ───────────────────────────────────────────
export const CitizenCaps = Object.freeze({ [GoonTypes.PEASANT_CAT]: 25, [GoonTypes.SCRIBE_CAT]: 25 })
// One-time payout for sending a citizen to school (they graduate, marked done).
export const CitizenEducationPayout = Object.freeze({ [GoonTypes.PEASANT_CAT]: 1000, [GoonTypes.SCRIBE_CAT]: 5000 })
// Friendly-fire: Press-1 shot that hits YOUR citizen instead of a goon → lose
// the citizen + this coin penalty.
export const FriendlyFirePenalty = Object.freeze({ [GoonTypes.PEASANT_CAT]: 1000, [GoonTypes.SCRIBE_CAT]: 5000 })
// (RETIRE GoonConfig.maxOwned 100 → use CitizenCaps. Keep GoonConfig.baseCost/
//  costGrowth for purchase price — unchanged this pass.)

// ── CR-8 per-citizen model: identity + trade promotions ─────────────────────
// Each owned citizen is an individual entity (id + name + type + role). A
// peasant can be promoted to a trade role — a civic honor (no coin change),
// recognizing your stewardship of the populace.
export const CitizenRoles = Object.freeze({
  FARMER:   { id: 'FARMER',   label: 'Farmer',   icon: '🌾', blurb: 'Works the rice paddies.' },
  MERCHANT: { id: 'MERCHANT', label: 'Merchant', icon: '⚖',  blurb: 'Trades goods at market.' },
  ARTISAN:  { id: 'ARTISAN',  label: 'Artisan',  icon: '🔨', blurb: 'Crafts wares by hand.' },
})
export const CitizenRoleOrder = ['FARMER', 'MERCHANT', 'ARTISAN']
// Names drawn (with a numeric suffix if needed) when a new citizen joins.
export const CitizenNamePool = Object.freeze([
  'Mochi', 'Tofu', 'Ginger', 'Bao', 'Pebble', 'Whisker', 'Jade', 'Plum', 'Bamboo',
  'Lychee', 'Soot', 'Tiger', 'Pumpkin', 'Sesame', 'Cloud', 'Pearl', 'Mei', 'Lotus',
  'Dumpling', 'Congee', 'Maple', 'Sprout', 'Inkpaw', 'Noodle', 'Peony', 'Ash',
  'Willow', 'Koi', 'Rune', 'Saffron', 'Biscuit', 'Yuan',
])

// ── Rice season (time-boxed; harvestable = min of the four season click counts) ──
export const RiceValue = 100 // coins per harvested rice at end of HARVEST
export const SeasonDurationsMs = Object.freeze({ // (tunable) per-season click window
  PREP: 18_000, PLANT: 22_000, MAINTAIN: 26_000, HARVEST: 22_000,
})
// (RETIRE the old click-GOAL season model + SeasonPaceMultiplier once CR-2 lands.)

// ── Mao self-education: 8 subjects × 3 levels (RETIRE old DegreeConfig+DiplomaConfig) ──
export const DegreeSubjects = Object.freeze([
  'POLY_SCIENCE', 'DIVINITY', 'AGRICULTURE', 'EDUCATION', 'HISTORY', 'FINE_ARTS', 'MILITARY', 'FINANCE',
])
export const DegreeSubjectLabels = Object.freeze({
  POLY_SCIENCE: 'Poly Science', DIVINITY: 'Divinity', AGRICULTURE: 'Agriculture', EDUCATION: 'Education',
  HISTORY: 'History', FINE_ARTS: 'Fine Arts', MILITARY: 'Military', FINANCE: 'Finance',
})
export const DegreeTiers = Object.freeze({
  CERT:    { id: 'CERT',    name: 'Certification', cost: 25_000 },
  MASTERS: { id: 'MASTERS', name: 'Masters',       cost: 100_000 },
  PHD:     { id: 'PHD',     name: 'PhD',           cost: 500_000 },
})
export const DegreeTierOrder = ['CERT', 'MASTERS', 'PHD'] // must earn in sequence per subject
// Each PhD permanently adds ClickEconomy.phdBonus to the Mao click base (CR-2).

// ── Real estate (RETIRE BuildingConfig.cost + PlotConfig.cost for purchase price) ──
export const RealEstatePrices = Object.freeze({
  LAND: 50_000,          // a plain plot
  HOME_SHACK: 0,         // free default (+1 rice field); valued at HOME_SHACK_BASE for net worth
  HOME_MIDDLE: 200_000,
  GRANARY: 75_000,
  SCHOOL: 100_000,
  SHOP: 250_000,
  TEMPLE: 500_000,
  HOSPITAL: 1_000_000,
  OTHER: 100_000,        // any other property type
})
export const HOME_SHACK_BASE = 25_000 // nominal net-worth value of the free shack
export const AssetAppreciationPerYear = 0.15 // value = basePrice × 1.15^(realYearsOwned)

// ── Honor / Ethics (0–10, shown in Registry; reputation-only for now) ───────
export const HonorConfig = Object.freeze({
  base: 5.0, min: 0, max: 10,
  perCert: 0.25, perMasters: 0.5, perPhd: 1.0, educationCap: 3.0, // Σ education contribution capped
  feedingMax: 1.5,   // up to +1.5 for recent/regular feeding cadence (tunable)
  lowTaxMax: 2.0,    // +lowTaxMax at 0% tax, scaling to 0 at max tax
  // Engagement / discipline — consistent play raises Honor; neglect loses it.
  // Measured as active days within a rolling window (piggybacks on login tracking).
  engagementMax: 1.5,        // up to +1.5 for regular play
  engagementWindowDays: 30,  // rolling window measured
  engagementIdealDays: 20,   // ~5 days/week of activity = full bonus
  abandonmentDays: 14,       // no activity this long →
  abandonmentPenalty: 1.0,   //   − this much (neglect/abandonment)
  starvationPenalty: 1.0,    // − for letting citizens starve (tunable)
  perTheftCaught: 1.0,       // − for each time the court caught you running off with a host's coffer …
  theftMax: 3.0,             // … at most this much
  // Civic leadership (CR-8): promoting citizens to trades raises Honor.
  civicMax: 1.0,             // Σ civic contribution capped
  perPromotion: 0.2,         // +per promoted citizen (5 promotions = full civicMax)
})

// ── Player-set tax (income ↔ Honor tradeoff; RETIRE automatic-only tribute) ──
export const TaxConfig = Object.freeze({
  minRate: 0, maxRate: 0.5, defaultRate: 0.1, // tribute multiplier add-on; Honor scales inversely
})


// ── CO-OP VISITS (docs/COOP-INVITES-PLAN.md) ────────────────────────────────────────────────────────────────────
// A guest in a host's kingdom works for the shared COFFER; the coffer is counted once a day (and when the stay ends): the guest takes their
// wage %, the host gets the rest. Every number is read by the server (server/stays.js) AND the Game Guide / visit screen.
export const CoopConfig = Object.freeze({
  maxGuests: 2,                 // guests a host can have at once
  countHours: 24,               // the coffer is counted this often
  guestRate: 0.5,               // a guest's Work earns this share of what the same Mao earns per click at home (set by the SERVER from the guest's saved game)
  workCooldownMs: 400,          // fastest a guest can press Work
  workDailyCap: 1500,           // Work presses per guest per day (a faucet must be capped)
  hostDepositDayMin: 50_000,    // the most coin a host can pay into the coffer in a day: the larger of this …
  hostDepositDayShare: 0.25,    // … and this share of their net worth (a modified game can't pour in endless coin)
  keepEndedHours: 72,           // how long a finished stay stays listed
  // invitations (server/invites.js reads these too; keep the wage range in step with the CHECK in server/sql/002_coop.sql)
  inviteExpiresHours: [1, 24, 72, 168], // how long an invitation can be accepted
  stayHours: [1, 4, 24],                // how long a guest may stay, in hours — the host picks one when inviting. No open-ended stays: a longer arrangement needs a fresh invitation
  defaultStayHours: 1,
  maxStayHours: 24,                     // no stay is ever longer than this (older invitations with a longer or open-ended stay are cut to this)
  wage: { min: 5, max: 15, def: 15 },   // the guest's share of the coffer, chosen by the host (%)
  maxPendingSent: 5,                    // invitations a host can have waiting
  retryAfterDeclineHours: 1,            // a host must wait this long before asking someone who declined again
  // the heist (a guest running off with the coffer)
  heistWindowMs: 30_000,                // the host has this long to catch a guest who ran
  heistMinCoffer: 100,                  // a heist needs at least this much in the coffer
  bonusShare: 0.5,                      // the court adds this share of the fine the guest pays to the host (the rest of the fine leaves the game)
  banDays: 7,                           // a caught thief cannot visit anyone for this long
  // against two accounts of one person passing coin to each other
  adoptMinHours: 24,                    // a Mao must have been adopted this long before it can invite or visit
  pairBudgetHours: 240,                 // the same host and guest can share at most this much time together (10 days) …
  pairBudgetDays: 30,                   // … in any rolling 30 days (a stay that would go over is cut short). The daily Work / deposit limits follow the PLAYER, not the stay
})
